export type PageBlock = {
  id: string;
  type: 'text' | 'image' | 'hero' | 'cta' | 'listV' | 'listH';
  x: number; y: number; w: number; h: number;
  content?: string; url?: string;
  // Optional image pan offsets (pixels) for 'image' blocks
  imgOffsetX?: number; imgOffsetY?: number;
  // Optional image zoom scale relative to cover scale (1 = cover)
  imgScale?: number;
  // Optional list items for list blocks
  listItems?: string[];
};
export type SectionModel = {
  title: string;
  rows?: number; columns?: number;
  gapX?: number; gapY?: number; radius?: number;
  colFractions?: number[];
  rowFractions?: number[];
  merges?: { r: number; c: number; rs: number; cs: number }[];
  blocks: PageBlock[];
};

// Backward-compatible: a PageModel can be a single-section shape (legacy)
// or a container with multiple sections. The editor will treat missing
// sections as a single implicit section based on the top-level fields.
export type PageModel = SectionModel & { sections?: SectionModel[] };

const BASES = (typeof window !== 'undefined' && window.location && window.location.port === '3000')
  ? ['http://localhost:8000']
  : [''];

async function tryFetch(path: string, init?: RequestInit) {
  let last: any = null;
  for (const b of BASES) {
    try {
      const url = b ? `${b}${path}` : path;
      const res = await fetch(url, init);
      if (res.ok) return res;
      last = new Error(`HTTP ${res.status}`);
    } catch (e) { last = e; }
  }
  throw last || new Error('Network error');
}

async function parseJson(res: Response) {
  const ct = (res.headers.get('content-type') || '').toLowerCase();
  if (ct.includes('application/json')) return await res.json();
  const text = await res.text().catch(() => '');
  const snippet = text ? text.slice(0, 200) : '';
  throw new Error(`Expected JSON but got ${ct || 'unknown content-type'}${snippet ? `: ${snippet}` : ''}`);
}

export async function savePage(slug: string, page: PageModel) {
  const body = JSON.stringify({ slug, page });
  const res = await tryFetch('/save_page.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
  return parseJson(res);
}

export async function getPage(slug: string): Promise<PageModel | null> {
  try {
    const res = await tryFetch(`/get_page.php?slug=${encodeURIComponent(slug)}`);
    const data = await parseJson(res);
    return data.page as PageModel;
  } catch { return null; }
}

export async function listPages(): Promise<{ slug: string; title?: string; size: number; mtime: number }[]> {
  const res = await tryFetch('/list_pages.php');
  const data = await parseJson(res);
  return data.items || [];
}

export async function deletePage(slug: string): Promise<boolean> {
  const res = await tryFetch('/delete_page.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slug }) });
  if (!res.ok) return false;
  const data = await parseJson(res);
  return !!data.ok;
}

export async function renamePage(oldSlug: string, newSlug: string, newTitle?: string): Promise<boolean> {
  const body = JSON.stringify({ oldSlug, newSlug, newTitle });
  const res = await tryFetch('/rename_page.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
  if (!res.ok) return false;
  const data = await parseJson(res);
  return !!data.ok;
}
